import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import moment from "moment";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import {
  EditIconNavButton,
  DeactivateIconButton,
  ActivateIconButton,
} from "../../../components/Buttons";
import {
  scrollToTop,
  TableEmptyMessage,
  USER_STATUS_IDS,
  USER_STATUS_LABELS,
} from "../../../components/Utils";
import ConfirmationDialog from "../../../components/ConfirmationDialog";

import { connect } from "react-redux";
import { frontloadConnect } from "react-frontload";
import {
  loadFilteredWorkers,
  activateWorker,
  deactivateWorker,
  defaultFilter,
} from "../actions";
import SalesTableUtil from "../../../components/table/SalesTableUtil";
import TablePageReport from "../../../components/TablePageReport";
import { PermissionResource, Resources } from "../../Permissions";
import { isResourceReadable } from "../../Permissions/actions";

const getFilterValues = (props, current) => {
  let values = { ...defaultFilter };
  if (current) {
    values = {
      ...values,
      rows: props.rows || defaultFilter.rows,
      ...current,
    };
  }
  return values;
};

const frontload = async (props) => {
  const filterValues = getFilterValues(props);
  await props.getWorkers(null, filterValues);
};
const mapStateToProps = (state) => {
  return {
    isLoading: state.worker.loading,
    header: state.worker.header,
    workers: state.worker.filteredWorkers,
    roles: state.worker.roles,
    permissions: state.permissions.permissionGroup.admindataaccess,
  };
};
const mapDispatchToProps = (dispatch) => ({
  getWorkers: (...args) => dispatch(loadFilteredWorkers(...args)),
  activateWorker: (...args) => dispatch(activateWorker(...args)),
  deactivateWorker: (...args) => dispatch(deactivateWorker(...args)),
});

const WorkerTable = (props) => {
  const {
    className = "",
    isLoading,
    workers,
    rows = 10,
    header,
    roles = [],
    permissions,
    disabled,
    getWorkers,
    activateWorker: activateWorkerAction,
    deactivateWorker: deactivateWorkerAction,
  } = props;

  const filterValuesRef = useRef(getFilterValues(props));
  const filterTimerIdRef = useRef(null);
  const dtRef = useRef(null);
  const dialogRef = useRef(null);

  const [first, setFirst] = useState(0);
  const [filteredCount, setFilteredCount] = useState((workers || []).length);

  useEffect(() => {
    scrollToTop();
  });

  const onEditWorker = (worker, history) => {
    history.push(`/wizard/organisation/mitarbeiter/${worker.id}`);
  };

  const onDeactivateWorker = (worker) => {
    dialogRef.current.show(
      true,
      {
        title: "Mitarbeiter deaktivieren",
        text: `Wollen Sie den Mitarbeiter ${worker.firstname} ${worker.lastname} wirklich deaktivieren? Die Löschung bestehender
          IT-Zugänge wird infolge beauftragt.`,
      },
      () => deactivateWorkerAction({ worker }),
    );
  };

  const onActivateWorker = (worker) => {
    dialogRef.current.show(
      true,
      {
        title: "Mitarbeiter aktivieren",
        text: `Wollen Sie den Mitarbeiter ${worker.firstname} ${worker.lastname} wirklich aktivieren?`,
      },
      () => activateWorkerAction(worker),
    );
  };

  const actionTemplate = (rowData) => {
    const canReadAdminData = isResourceReadable(
      Resources.AdminDataAccess,
      permissions,
    );
    const editId = `editWorkerBtn${rowData.id}`;
    const editBtn = (
      <PermissionResource resource={Resources.Organisation.WorkerWizard.Page}>
        <>
          <EditIconNavButton
            id={editId}
            name={editId}
            onClick={(history) => onEditWorker(rowData, history)}
          />
        </>
      </PermissionResource>
    );
    const actId = `actWorkerBtn${rowData.id}`;
    const activateBtn = !disabled &&
      rowData.status_id === USER_STATUS_IDS.DEACTIVATED && (
        <ActivateIconButton
          id={actId}
          name={actId}
          onClick={() => onActivateWorker(rowData)}
          disabled={!canReadAdminData}
        />
      );
    const deactId = `deactWorkerBtn${rowData.id}`;
    const deactivateBtn = !disabled &&
      rowData.status_id !== USER_STATUS_IDS.DEACTIVATED && (
        <DeactivateIconButton
          id={deactId}
          name={deactId}
          onClick={() => onDeactivateWorker(rowData, canReadAdminData)}
          disabled={rowData.status_id !== USER_STATUS_IDS.APPROVED}
        />
      );
    return (
      <div>
        {editBtn}
        <PermissionResource
          resource={Resources.Organisation.WorkerWizard.BasicInfo}
          showOnlyIfEditable
        >
          {activateBtn}
          {deactivateBtn}
        </PermissionResource>
      </div>
    );
  };

  const onLazyLoad = (event) => {
    const filterValues = { ...filterValuesRef.current };
    filterValues.first = event.first;
    filterValues.rows = event.rows;
    getWorkers(() => setFirst(event.first), filterValues);
  };

  const onValueChange = (nextValue) => {
    if (nextValue) {
      setFirst(0);
      setFilteredCount(nextValue.length);
    }
  };

  const onFilterChange = (field, value, noTimeout, noFilter) => {
    // console.log("WorkerTable.onFilterChange()", field, value, noTimeout);
    filterValuesRef.current[field] = value;
    !noFilter && setFirst(0);
    if (noTimeout) {
      return !noFilter && getWorkers(null, filterValuesRef.current);
    }
    if (filterTimerIdRef.current) {
      clearTimeout(filterTimerIdRef.current);
    }
    filterTimerIdRef.current = setTimeout(() => {
      !noFilter && getWorkers(null, filterValuesRef.current);
    }, 1000);
  };

  const onSort = (event) => {
    filterValuesRef.current.sortFields = event.multiSortMeta.map((item) => ({
      ...item,
    }));
    getWorkers(null, { ...filterValuesRef.current });
  };

  if (!workers) {
    return null;
  }

  const totalRecords = Number(
    header ? header["x-result-count"] : workers.length,
  );
  const data = (workers || []).map((worker, index) => {
    let role = "",
      companyName = "";
    if (worker.companies && worker.companies.length > 0) {
      // if (worker.companies.length === 1) {
      //   companyName = worker.companies[0].name;
      //   if (worker.companies[0].roles && worker.companies[0].roles.length > 0) {
      //     worker.companies[0].roles.sort((a, b) => a.role_id - b.role_id);
      //     role = worker.companies[0].roles[0].role_label;
      //   }
      //   if (!role || role === '') {
      //     role = worker.companies[0].role_label || 'Mitarbeiter'; // alternatively extended roles
      //   }
      // } else {
      //   companyName = [];
      //   worker.companies.forEach(pCompany => {
      //     if (pCompany.role_label) { role = pCompany.role_label; }
      //     companyName.push(pCompany.name);
      //   });
      //   companyName = companyName.join(', ');
      // }
      role = [];
      companyName = [];
      worker.companies.forEach((pCompany) => {
        // console.log("CompanyRoles", pCompany.roles);
        role = role.concat(pCompany.roles || []);
        companyName.push(pCompany.name);
      });
      role = role.map((item) => item.role_label).join(", ");
      companyName = companyName.join(", ");
    }
    return {
      ...worker,
      index,
      role,
      companyName,
      modified: moment(
        worker.modified_date || worker.created_date,
        "YYYY-MM-DDTHH:mm:ss:SSSZ",
      ).format("DD.MM.YYYY HH:mm"),
      status_id: worker.status_id || USER_STATUS_IDS.APPROVED,
      status:
        worker.status ||
        USER_STATUS_LABELS[worker.status_id || USER_STATUS_IDS.APPROVED],
    };
  });
  const role = roles.map((item) => ({
    label: item.label,
    value: item.label,
  }));
  const status_id = [
    {
      label: USER_STATUS_LABELS[USER_STATUS_IDS.APPROVED],
      value: USER_STATUS_IDS.APPROVED,
    },
    {
      label: USER_STATUS_LABELS[USER_STATUS_IDS.DEACTIVATED],
      value: USER_STATUS_IDS.DEACTIVATED,
    },
    {
      label: USER_STATUS_LABELS[USER_STATUS_IDS.DEACTIVATION_PENDING],
      value: USER_STATUS_IDS.DEACTIVATION_PENDING,
    },
  ];
  const optionMap = { role, status_id };

  // filters
  const useInputFilter = SalesTableUtil.createUseInputFilter(
    dtRef.current,
    400,
    onFilterChange,
  );
  const useMultiSelectFilter = SalesTableUtil.createUseMultiSelectFilter(
    dtRef.current,
    data,
    optionMap,
    onFilterChange,
  );
  const useRangeFilter = SalesTableUtil.createUseRangeFilter(
    dtRef.current,
    onFilterChange,
  );
  const usernameFilter = useInputFilter("username", { filterOnChange: true });
  const roleFilter = useMultiSelectFilter("role");
  const companyNameFilter = useInputFilter("companyName", {
    filterOnChange: true,
  });
  const modifiedFilter = useRangeFilter("modified");
  const statusFilter = useMultiSelectFilter("status_id");
  const columnStyle = {
    verticalAlign: "top",
    paddingTop: "16px",
    paddingBottom: "16px",
  };

  return (
    <div className={`worker-table clearfix ${className}`}>
      <DataTable
        value={data}
        rows={rows}
        ref={dtRef}
        onValueChange={onValueChange}
        first={first}
        lazy={true}
        totalRecords={totalRecords}
        onPage={(event) => onLazyLoad(event)}
        paginator
        paginatorLeft={
          <TablePageReport
            className="pad-lft"
            totalRecords={totalRecords}
            filteredRecords={filteredCount}
            first={first}
            rows={rows}
            emptyText="Keine Mitarbeiter"
            itemName="Mitarbeiter"
            itemNamePlural="Mitarbeiter"
          />
        }
        removableSort
        sortMode="multiple"
        multiSortMeta={
          data.length <= 1 ? [] : filterValuesRef.current.sortFields
        }
        onSort={onSort}
        emptyMessage={
          isLoading ? null : <TableEmptyMessage itemNamePlural="Mitarbeiter" />
        }
      >
        <Column
          field="username"
          header="Mitarbeiter"
          sortable={data.length > 1}
          filter
          {...usernameFilter}
          style={columnStyle}
        />
        <Column
          field="role"
          header="Position"
          filter
          {...roleFilter}
          style={columnStyle}
          body={(item) => (
            <>
              {item.role.split(", ").map((role) => (
                <label key={role} className="w3-block">
                  {role}
                </label>
              ))}
            </>
          )}
        />
        <Column
          field="companyName"
          header="Beschäftigungsfirma"
          filter
          {...companyNameFilter}
          style={columnStyle}
        />
        <Column
          field="modified"
          header="Letzte Änderung"
          filter
          {...modifiedFilter}
          style={{ ...columnStyle, textAlign: "center", width: "12em" }}
        />
        <Column
          field="status_id"
          header="Status"
          sortable={data.length > 1}
          filter
          {...statusFilter}
          body={(rowData) => rowData.status}
          style={{ ...columnStyle, textAlign: "center", width: "10em" }}
        />
        <Column
          body={actionTemplate}
          style={{
            verticalAlign: "top",
            textAlign: "center",
            width: "7.5em",
          }}
        />
      </DataTable>
      <ConfirmationDialog ref={dialogRef} />
    </div>
  );
};

WorkerTable.propTypes = {
  workers: PropTypes.array,
  roles: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
      label: PropTypes.string,
    }),
  ),
  activateWorker: PropTypes.func.isRequired,
  deactivateWorker: PropTypes.func.isRequired,
  rows: PropTypes.number,
  permissions: PropTypes.shape({}),
  className: PropTypes.string,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(
  frontloadConnect(frontload, {
    onMount: true,
    onUpdate: false,
  })(WorkerTable),
);
